/**
 * Helpers for the work-item dispatch hints (preferred_provider / preferred_model).
 *
 * The option catalog is single-sourced in `shared/config/sf_dispatch_providers.config.json`
 * (imported here via the `shared-config` symlink), which mirrors PanClaude's
 * `src/shared/providers.ts` registry. Providers and their models are keyed maps with a
 * `sort_order`; keys starting with `$` (e.g. `$comment`) are metadata, not providers.
 */

interface DispatchModelMeta {
  label: string;
  sort_order: number;
  /** Narrows (or, when empty, disables) the provider's thinking ladder for this model. */
  thinking_levels?: string[];
}

interface DispatchProviderMeta {
  label: string;
  sort_order: number;
  /** Reasoning-effort levels this provider's CLI accepts, in ladder order. */
  thinking_levels?: string[];
  models: Record<string, DispatchModelMeta>;
}

export type DispatchProvidersConfig = Record<string, DispatchProviderMeta | string>;

function providerMap(config: DispatchProvidersConfig): Record<string, DispatchProviderMeta> {
  const out: Record<string, DispatchProviderMeta> = {};
  for (const [key, value] of Object.entries(config)) {
    if (key.startsWith('$') || typeof value === 'string') continue;
    out[key] = value;
  }
  return out;
}

/** Provider ids in configured display order. */
export function dispatchProviderIds(config: DispatchProvidersConfig): string[] {
  const map = providerMap(config);
  return Object.keys(map).sort((a, b) => map[a].sort_order - map[b].sort_order);
}

/** Model ids for a provider in configured display order ([] for an unknown/empty provider). */
export function dispatchModelIds(config: DispatchProvidersConfig, provider: string): string[] {
  const models = providerMap(config)[provider]?.models ?? {};
  return Object.keys(models).sort((a, b) => models[a].sort_order - models[b].sort_order);
}

/** Display label for a provider id (falls back to the id itself). */
export function dispatchProviderLabel(config: DispatchProvidersConfig, provider: string): string {
  return providerMap(config)[provider]?.label ?? provider;
}

/** Display label for a model id under a provider (falls back to the id itself). */
export function dispatchModelLabel(
  config: DispatchProvidersConfig,
  provider: string,
  model: string,
): string {
  return providerMap(config)[provider]?.models?.[model]?.label ?? model;
}

/**
 * Thinking levels offered for a provider, narrowed by the model when the model says so.
 *
 * A model entry may declare its own `thinking_levels` to narrow — or, with an empty list,
 * to disable — the provider's ladder (Haiku has no effort knob at all). An unknown model
 * falls back to the provider's list, so a model id PanClaude added before this mirror
 * caught up is not blocked from its provider's levels. Unknown provider → [].
 */
export function dispatchThinkingLevels(
  config: DispatchProvidersConfig,
  provider: string,
  model?: string | null,
): string[] {
  const entry = providerMap(config)[provider];
  if (!entry) return [];
  const modelLevels = model ? entry.models?.[model]?.thinking_levels : undefined;
  return modelLevels ?? entry.thinking_levels ?? [];
}
